import { onMounted, onUnmounted, ref } from 'vue'
import { bareHttp } from '@/core/api/http'

/** Re-check cadence; long-lived tabs must still see a new deploy. Hidden tabs skip polls. */
const POLL_MS = 120_000
/** Focus / visibility checks are cheap but bursty (tab switching). */
const EVENT_THROTTLE_MS = 15_000
const INITIAL_DELAY_MS = 8_000
const STANDALONE_DELAY_MS = 250
const RELOAD_MARK = '_appv'
const FORCE_MARK = 'app_update'
const TRIED_KEY = 'hr_recruitment_appv_tried'
const APP_KEY = 'recruitment-web'

const updateAvailable = ref(false)
let subscribers = 0
let timer: ReturnType<typeof setTimeout> | null = null
let initial: ReturnType<typeof setTimeout> | null = null
let checking = false
let applying = false
let pendingApply = false
let lastCheckAt = 0
let lastRemoteSeen = ''

function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches
    || nav.standalone === true
}

function localBuildId(): string {
  return String(import.meta.env.VITE_APP_BUILD_ID ?? '')
}

function versionUrl(): string {
  const base = import.meta.env.BASE_URL || '/'
  const normalized = base.endsWith('/') ? base : `${base}/`
  return `${normalized}version.json`
}

function stripReloadMarker(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has(RELOAD_MARK)) return
  url.searchParams.delete(RELOAD_MARK)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

stripReloadMarker()

if (typeof window !== 'undefined') {
  // Support check from DevTools: compare with GET /api/v1/client-apps/recruitment-web/version.
  ;(window as Window & { __APP_BUILD_ID__?: string }).__APP_BUILD_ID__ = localBuildId()
}

function readTriedRemote(): string {
  try {
    return sessionStorage.getItem(TRIED_KEY) ?? ''
  } catch {
    return ''
  }
}

function markTriedRemote(remote: string): void {
  if (!remote) return
  try {
    sessionStorage.setItem(TRIED_KEY, remote)
  } catch {
    // private mode
  }
}

async function fetchApiBuildId(): Promise<string> {
  const { data } = await bareHttp.get<{ data?: { build_id?: unknown } }>(
    `/client-apps/${APP_KEY}/version`,
    { params: { t: Date.now() } },
  )
  const remote = data?.data?.build_id
  return typeof remote === 'string' ? remote.trim() : ''
}

async function fetchFileBuildId(): Promise<string> {
  const res = await fetch(`${versionUrl()}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return ''
  const payload = (await res.json()) as { buildId?: unknown }
  return typeof payload.buildId === 'string' ? payload.buildId.trim() : ''
}

function forceFromUrl(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.search.includes(`${FORCE_MARK}=`)
}

async function remoteBuildId(): Promise<string> {
  try {
    const fromApi = await fetchApiBuildId()
    if (fromApi) return fromApi
  } catch {
    // API down or table not migrated yet — fall back to version.json
  }
  try {
    return await fetchFileBuildId()
  } catch {
    return ''
  }
}

function queueApply(remote: string): void {
  pendingApply = true
  applying = true
  markTriedRemote(remote)
  void applyAppUpdate(remote)
  window.setTimeout(() => {
    if (!pendingApply) return
    applying = false
    updateAvailable.value = true
  }, 2500)
}

async function check(fromPoll = false): Promise<void> {
  // Vite rebuilds get a new build id every restart; never match published API versions.
  if (import.meta.env.DEV && !forceFromUrl()) return
  if (updateAvailable.value || checking || applying || pendingApply) return
  if (!fromPoll && lastCheckAt && Date.now() - lastCheckAt < EVENT_THROTTLE_MS) return
  if (forceFromUrl()) {
    updateAvailable.value = true
    return
  }
  const local = localBuildId()
  if (!local) return
  checking = true
  lastCheckAt = Date.now()
  try {
    const remote = await remoteBuildId()
    if (!remote || remote === local) return
    // Already reloaded for this remote in this tab — avoid infinite prompt loop
    // when static assets / API publish are temporarily out of sync.
    if (readTriedRemote() === remote) return
    lastRemoteSeen = remote
    if (isStandalonePwa()) {
      queueApply(remote)
      return
    }
    updateAvailable.value = true
  } finally {
    checking = false
  }
}

function onVisible(): void {
  if (document.visibilityState === 'visible') void check()
}

function onPageShow(event: PageTransitionEvent): void {
  if (event.persisted || document.visibilityState === 'visible') void check()
}

function onInteract(): void {
  if (pendingApply || applying) void applyAppUpdate(lastRemoteSeen || readTriedRemote())
}

function schedulePoll(): void {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    const run = document.visibilityState === 'visible' ? check(true) : Promise.resolve()
    void run.finally(() => {
      schedulePoll()
    })
  }, POLL_MS)
}

function startWatching(): void {
  if (initial || timer) return
  if (import.meta.env.DEV && !forceFromUrl()) return
  const delay = isStandalonePwa() ? STANDALONE_DELAY_MS : INITIAL_DELAY_MS
  initial = setTimeout(() => {
    void check()
    schedulePoll()
  }, delay)
  document.addEventListener('visibilitychange', onVisible)
  document.addEventListener('pointerdown', onInteract, { passive: true })
  document.addEventListener('touchstart', onInteract, { passive: true })
  window.addEventListener('focus', onVisible)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('online', onVisible)
}

function stopWatching(): void {
  if (initial) {
    clearTimeout(initial)
    initial = null
  }
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  document.removeEventListener('visibilitychange', onVisible)
  document.removeEventListener('pointerdown', onInteract)
  document.removeEventListener('touchstart', onInteract)
  window.removeEventListener('focus', onVisible)
  window.removeEventListener('pageshow', onPageShow)
  window.removeEventListener('online', onVisible)
}

export async function applyAppUpdate(remote = lastRemoteSeen || readTriedRemote()): Promise<void> {
  pendingApply = true
  applying = true
  if (remote) markTriedRemote(remote)
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((registration) => registration.unregister().catch(() => false)))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // still hard-reload
  }
  const next = new URL(window.location.href)
  next.searchParams.delete(FORCE_MARK)
  next.searchParams.set(RELOAD_MARK, String(Date.now()))
  const href = next.toString()
  const before = window.location.href
  try {
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } catch {
    // fall through
  }
  window.location.href = href
  window.setTimeout(() => {
    if (window.location.href !== before) return
    window.location.href = `${window.location.origin}/?${RELOAD_MARK}=${Date.now()}`
  }, 400)
  window.setTimeout(() => {
    if (window.location.href !== before) return
    window.location.reload()
  }, 800)
}

export function useAppUpdate() {
  onMounted(() => {
    subscribers += 1
    if (subscribers === 1) startWatching()
  })

  onUnmounted(() => {
    subscribers = Math.max(0, subscribers - 1)
    if (subscribers === 0) stopWatching()
  })

  return { updateAvailable, applyUpdate: applyAppUpdate }
}
